// API Service for Shakespeare.diy Backend
// Replace with your Shakespeare.diy URL after deployment (e.g., https://your-project.shakespeare.diy/api)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Helper function to make requests
const apiRequest = async (endpoint, options = {}) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;

    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit', // Don't send credentials for CORS
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    });

    clearTimeout(timeoutId);

    // Get response text
    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    // Parse JSON if we got a response
    const data = responseText ? JSON.parse(responseText) : null;
    return { success: true, data };
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('API timeout:', endpoint);
      return { success: false, error: 'Connection timeout' };
    }
    console.warn('API request failed:', endpoint, error.message);
    return { success: false, error: error.message };
  }
};

// ============================================================================
// BOUNTIES
// ============================================================================

/**
 * Get all bounties for a meetup
 * GET /bounties/:meetupId
 */
export const getBounties = async (meetupId = 'taipei') => {
  return apiRequest(`/bounties/${meetupId}`, {
    method: 'GET',
  });
};

/**
 * Create a new bounty
 * POST /bounties
 */
export const createBounty = async (bountyData) => {
  return apiRequest('/bounties', {
    method: 'POST',
    body: JSON.stringify(bountyData),
  });
};

/**
 * Update bounty status (claimed, completed, etc.)
 * POST /update-bounty
 */
export const updateBounty = async (bountyId, updates) => {
  return apiRequest(`/bounties/${bountyId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
};

// ============================================================================
// CONTRIBUTORS
// ============================================================================

/**
 * Add contribution to a bounty
 * POST /contributions
 */
export const addContribution = async (contributionData) => {
  return apiRequest('/contributions', {
    method: 'POST',
    body: JSON.stringify(contributionData),
  });
};

/**
 * Get contributors for a specific bounty
 * GET /get-contributors?bountyId=123
 */
export const getContributors = async (bountyId) => {
  return apiRequest(`/contributions?bounty_id=${bountyId}`, { method: 'GET' });
};

// ============================================================================
// CLAIMS
// ============================================================================

/**
 * Claim a bounty
 * POST /claim-bounty
 */
export const claimBounty = async (claimData) => {
  return apiRequest('/claims', {
    method: 'POST',
    body: JSON.stringify(claimData),
  });
};

/**
 * Get all claims (optionally filtered by status)
 * GET /get-claims
 */
export const getClaims = async (status = null) => {
  const endpoint = status ? `/claims?status=${status}` : '/claims';
  return apiRequest(endpoint, { method: 'GET' });
};

// ============================================================================
// VERIFICATIONS
// ============================================================================

/**
 * Add verification to a claim
 * POST /add-verification
 */
export const addVerification = async (verificationData) => {
  return apiRequest('/verifications', {
    method: 'POST',
    body: JSON.stringify(verificationData),
  });
};

/**
 * Get verifications for a specific claim
 * GET /get-verifications?claimId=123
 */
export const getVerifications = async (claimId) => {
  return apiRequest(`/verifications?claim_id=${claimId}`, { method: 'GET' });
};

// ============================================================================
// COMMENTS
// ============================================================================

/**
 * Add comment to a bounty
 * POST /add-comment
 */
export const addComment = async (commentData) => {
  return apiRequest('/comments', {
    method: 'POST',
    body: JSON.stringify(commentData),
  });
};

/**
 * Get comments for a specific bounty
 * GET /get-comments?bountyId=123
 */
export const getComments = async (bountyId) => {
  return apiRequest(`/comments?bounty_id=${bountyId}`, { method: 'GET' });
};

// ============================================================================
// WATCHERS
// ============================================================================

/**
 * Add or update watch preferences
 * POST /set-watch-preferences
 */
export const setWatchPreferences = async (watchData) => {
  return apiRequest('/set-watch-preferences', {
    method: 'POST',
    body: JSON.stringify(watchData),
  });
};

/**
 * Get watch preferences for an email
 * GET /get-watch-preferences?email=user@example.com
 */
export const getWatchPreferences = async (email) => {
  return apiRequest(`/get-watch-preferences?email=${encodeURIComponent(email)}`, { method: 'GET' });
};

// ============================================================================
// MEETUPS
// ============================================================================

/**
 * Get all meetups
 * GET /get-meetups
 */
export const getMeetups = async () => {
  return apiRequest('/meetups', { method: 'GET' });
};

/**
 * Request new meetup (pending approval)
 * POST /request-meetup
 */
export const requestMeetup = async (meetupData) => {
  return apiRequest('/request-meetup', {
    method: 'POST',
    body: JSON.stringify(meetupData),
  });
};

// ============================================================================
// ADMINS
// ============================================================================

/**
 * Admin login
 * POST /admin-login
 */
export const adminLogin = async (email, password) => {
  return apiRequest('/admin-login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
};

/**
 * Change admin password
 * POST /change-password
 */
export const changePassword = async (email, newPassword) => {
  return apiRequest('/change-password', {
    method: 'POST',
    body: JSON.stringify({ email, newPassword }),
  });
};

/**
 * Forgot password - send email
 * POST /forgot-password
 */
export const forgotPassword = async (email) => {
  return apiRequest('/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

// ============================================================================
// SIGN UP (Landing Page)
// ============================================================================

/**
 * User sign up from landing page
 * POST /sign-up
 */
export const signUp = async (userData) => {
  return apiRequest('/sign-up', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get all data for a specific meetup (bounties, claims, etc.)
 * This is a convenience function that makes multiple API calls
 */
export const getMeetupData = async (meetupId) => {
  const [bounties, claims] = await Promise.all([
    getBounties(meetupId),
    getClaims(),
  ]);

  return {
    bounties: bounties.success ? bounties.data : [],
    claims: claims.success ? claims.data : [],
  };
};

/**
 * Get full bounty details with contributors and comments
 */
export const getBountyDetails = async (bountyId) => {
  const [contributors, comments] = await Promise.all([
    getContributors(bountyId),
    getComments(bountyId),
  ]);

  return {
    contributors: contributors.success ? contributors.data : [],
    comments: comments.success ? comments.data : [],
  };
};

export default {
  getBounties,
  createBounty,
  updateBounty,
  addContribution,
  getContributors,
  claimBounty,
  getClaims,
  addVerification,
  getVerifications,
  addComment,
  getComments,
  setWatchPreferences,
  getWatchPreferences,
  getMeetups,
  requestMeetup,
  adminLogin,
  changePassword,
  forgotPassword,
  signUp,
  getMeetupData,
  getBountyDetails,
};
